import Button from "components/base/Button";
import { Input, Select } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import { useState } from "react";

const toComparableNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCompareRowState = (item) => {
  const wisQty = toComparableNumber(item?.wis_qty);
  const alcAvailableQty = toComparableNumber(item?.alc_available_qty);

  const hasMismatchWithCheckData =
    wisQty !== null && alcAvailableQty !== null && wisQty !== alcAvailableQty;

  return { hasMismatchWithCheckData };
};

const getDefaultAdjustQty = (item) => {
  const wisQty = toComparableNumber(item?.wis_qty);
  const alcAvailableQty = toComparableNumber(item?.alc_available_qty);
  if (wisQty === null || alcAvailableQty === null) return "";
  return `${alcAvailableQty - wisQty}`;
};

const StockCompareView = ({ stocks, onSelect = () => {} }) => {
  const [q, setQ] = useState("");
  const [viewType, setViewType] = useState("mismatch");
  const [selectedMap, setSelectedMap] = useState(() => {
    const map = {};
    stocks.forEach((item) => {
      const { hasMismatchWithCheckData } = getCompareRowState(item);
      map[item.product_id] = {
        selected: hasMismatchWithCheckData,
        adjustQty: getDefaultAdjustQty(item),
      };
    });
    return map;
  });
  const normalizedQ = q.trim().toLowerCase();

  const sortedRows = [...stocks].sort((a, b) =>
    String(a?.sku || "").localeCompare(String(b?.sku || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
  const filteredRows = sortedRows.filter((item) => {
    const { hasMismatchWithCheckData } = getCompareRowState(item);
    if (viewType === "mismatch" && !hasMismatchWithCheckData) return false;
    if (!normalizedQ) return true;
    const number = String(item?.sku || "").toLowerCase();
    const name = String(item?.product_name || "").toLowerCase();
    const alcSku = String(item?.alc_sku || "").toLowerCase();
    const shopifySku = String(item?.shopify_sku || "").toLowerCase();
    return (
      number.includes(normalizedQ) ||
      name.includes(normalizedQ) ||
      alcSku.includes(normalizedQ) ||
      shopifySku.includes(normalizedQ)
    );
  });
  const selectedRows = sortedRows.filter(
    (item) => selectedMap[item.product_id]?.selected,
  );
  const isAllFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((item) => selectedMap[item.product_id]?.selected);

  return (
    <div className="h-full flex flex-col p-10 text-sm">
      <div className="mb-4 flex items-center space-x-4">
        <Select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="mismatch">Mismatched Products</option>
          <option value="all">All Products</option>
        </Select>
        <div className="flex-1">
          <SearchBar
            initialQuery={q}
            placeholder="Search by product number or name"
            onChange={setQ}
          />
        </div>
      </div>

      <div className="card p-0 flex-1 overflow-auto flex flex-col">
        <div className="flex-1 overflow-auto relative">
          {filteredRows.length === 0 ? (
            <div className="p-8 text-sm text-gray-500">
              No products for the selected view and search query.
            </div>
          ) : (
            <div className="whitespace-nowrap">
              <table>
                <thead>
                  <tr className="sticky top-0 z-10 backdrop-blur bg-gray-50 bg-opacity-80 dark:bg-gray-700/50">
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isAllFilteredSelected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedMap((prev) => {
                            const next = { ...prev };
                            filteredRows.forEach((item) => {
                              next[item.product_id] = {
                                ...(next[item.product_id] || {}),
                                selected: checked,
                                adjustQty:
                                  next[item.product_id]?.adjustQty ??
                                  getDefaultAdjustQty(item),
                              };
                            });
                            return next;
                          });
                        }}
                      />
                    </th>
                    <th className="px-4 py-3">
                      Product({filteredRows.length})
                    </th>
                    <th className="px-4 text-right">ALC Tech Col</th>
                    <th className="px-4 text-right">ALC Avail Col</th>
                    <th className="px-4 text-right">WIS</th>
                    <th className="px-4 text-right">Adjust Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((item) => {
                    const { hasMismatchWithCheckData } =
                      getCompareRowState(item);

                    return (
                      <tr
                        key={item.product_id}
                        className={`border-y border-gray-100 dark:border-gray-700 ${
                          hasMismatchWithCheckData
                            ? " text-red-600  dark:text-red-300"
                            : ""
                        } hover:bg-sky-50 dark:hover:bg-sky-900/20`}
                      >
                        <td className="px-4">
                          <input
                            type="checkbox"
                            checked={Boolean(
                              selectedMap[item.product_id]?.selected,
                            )}
                            onChange={(e) =>
                              setSelectedMap((prev) => ({
                                ...prev,
                                [item.product_id]: {
                                  ...(prev[item.product_id] || {}),
                                  selected: e.target.checked,
                                  adjustQty:
                                    prev[item.product_id]?.adjustQty ??
                                    getDefaultAdjustQty(item),
                                },
                              }))
                            }
                          />
                        </td>
                        <td className="px-4">
                          [{item.sku}] {item.product_name} ({item.items_per_set}
                          /ctn)
                        </td>
                        <td className="px-4 text-right">
                          {item.alc_tech_qty ?? "-"}
                        </td>
                        <td className="px-4 text-right">
                          {item.alc_available_qty ?? "-"}
                        </td>
                        <td className="px-4 text-right">{item.wis_qty}</td>
                        <td className="px-4 text-right">
                          <Input
                            type="number"
                            className="text-right"
                            value={
                              selectedMap[item.product_id]?.adjustQty ?? ""
                            }
                            disabled={!selectedMap[item.product_id]?.selected}
                            onChange={(e) =>
                              setSelectedMap((prev) => ({
                                ...prev,
                                [item.product_id]: {
                                  ...(prev[item.product_id] || {}),
                                  selected:
                                    prev[item.product_id]?.selected ?? false,
                                  adjustQty: e.target.value,
                                },
                              }))
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          primary
          disabled={selectedRows.length === 0}
          onClick={() =>
            onSelect(
              selectedRows.map((item) => ({
                ...item,
                adjustQty: selectedMap[item.product_id]?.adjustQty ?? "",
              })),
            )
          }
        >
          Add Selected Products for Inventory Adjustment selected (
          {selectedRows.length})
        </Button>
      </div>
    </div>
  );
};

export default StockCompareView;
